import { Link, useNavigate } from '@tanstack/react-router';
import { Plus, TriangleAlert } from 'lucide-react';
import { useState, type KeyboardEvent, type ReactNode } from 'react';
import type { InterviewKitView, RubricView } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Chip } from '../../../../components/Chip';
import { Field, FieldRow } from '../../../../components/Field';
import { Input, Textarea } from '../../../../components/Input';
import { KitQuestionList } from '../../../../components/KitQuestionList';
import { Select } from '../../../../components/Select';
import { errorMessage } from '../../../../lib/api-client';
import { useCanEdit } from '../../../../stores/session';
import { useToasts } from '../../../../stores/toasts';
import { useSaveInterviewKit } from '../../api/evaluation-api';
import { useLeaveGuard } from './LeaveGuard';
import { MoveMenu, moveItem, useDragReorder } from './reorder';
import styles from './Evaluation.module.css';

/**
 * ADMIN-04, one interview kit (admin redesign batch 2, Option A). The questions sit under the rubric line each gathers
 * evidence for — the group a question is in is its line, replacing the old "Evidence for" picker on every question. A line
 * with no questions shows in amber. Questions move by dragging (into another line's group changes their line), Alt with ↑/↓
 * on the handle, or the "Move to…" menu. Choosing another rubric keeps a question only if the new rubric has its line; the
 * rest are listed to re-home first. Beside it, the interviewer's screen, drawn by the cockpit's own list.
 *
 * Every question keeps its key across versions (migration 0069): the save sends it.
 */

export const MAX_KIT_QUESTIONS = 40;

interface QuestionDraft {
  readonly rowId: string;
  readonly key: string | undefined;
  readonly questionText: string;
  readonly guidance: string;
  /** The rubric line by its key; one the chosen rubric doesn't have waits in "To re-home". */
  readonly criterionKey: string | null;
}

interface Draft {
  readonly name: string;
  readonly description: string;
  readonly rubricFamilyId: string | null;
  readonly questions: readonly QuestionDraft[];
}

function draftOf(kit: InterviewKitView | null): Draft {
  return {
    name: kit?.name ?? '',
    description: kit?.description ?? '',
    rubricFamilyId: kit?.rubricFamilyId || null,
    questions: (kit?.questions ?? []).map((question) => ({
      rowId: question.key,
      key: question.key,
      questionText: question.questionText,
      guidance: question.guidance ?? '',
      criterionKey: question.criterionKey || null,
    })),
  };
}

function newQuestion(criterionKey: string): QuestionDraft {
  return { rowId: crypto.randomUUID(), key: undefined, questionText: '', guidance: '', criterionKey };
}

/** The questions as they stand on screen: the ones to re-home first, then each line's, in the rubric's order. */
export function inLineOrder(questions: readonly QuestionDraft[], lineKeys: readonly string[]): readonly QuestionDraft[] {
  const rank = (question: QuestionDraft): number => (question.criterionKey === null ? -1 : lineKeys.indexOf(question.criterionKey));
  return questions
    .map((question, index) => ({ question, index }))
    .sort((a, b) => rank(a.question) - rank(b.question) || a.index - b.index)
    .map(({ question }) => question);
}

/** What still stops a save, in plain words; empty when it can go. */
export function kitProblems(draft: Draft, rubric: RubricView | null): readonly string[] {
  const keys = new Set((rubric?.criteria ?? []).map((line) => line.key));
  const problems: string[] = [];
  if (draft.name.trim().length < 2) problems.push('Name the kit.');
  if (!rubric) problems.push('Choose the rubric its questions are for.');
  if (draft.questions.length === 0) problems.push('Add at least one question.');
  if (draft.questions.some((question) => question.questionText.trim() === '')) problems.push('Every question needs its wording.');
  if (rubric && draft.questions.some((question) => question.criterionKey === null || !keys.has(question.criterionKey))) problems.push('Move each question waiting to be re-homed to a line, or remove it.');
  return problems;
}

export function KitEditor({ kit, current, rubrics }: { readonly kit: InterviewKitView | null; readonly current: InterviewKitView | null; readonly rubrics: readonly RubricView[] }): ReactNode {
  const canEdit = useCanEdit('hiring_setup');
  const save = useSaveInterviewKit();
  const navigate = useNavigate();
  const show = useToasts((state) => state.show);
  const [base] = useState(kit);
  const [draft, setDraft] = useState<Draft>(() => draftOf(kit));
  const [isDirty, setIsDirty] = useState(false);
  const [focused, setFocused] = useState<string | null>(null);
  const [openGuidance, setOpenGuidance] = useState<ReadonlySet<string>>(new Set());
  const { prompt, release } = useLeaveGuard(isDirty);

  const isOlder = base !== null && !base.isCurrent;
  // Our own save replaces this version too, a moment before its answer is back; that is not someone else's.
  const [isSaving, setIsSaving] = useState(false);
  const isOvertaken = base !== null && base.isCurrent && kit !== null && !kit.isCurrent && !isSaving;
  const isReadOnly = !canEdit || isOlder || isOvertaken;
  const rubric = rubrics.find((candidate) => candidate.familyId === draft.rubricFamilyId) ?? null;
  const lines = rubric?.criteria ?? [];
  const lineKeys = lines.map((line) => line.key);
  const questions = inLineOrder(draft.questions, lineKeys);
  const isHomed = (question: QuestionDraft): boolean => question.criterionKey !== null && lineKeys.includes(question.criterionKey);
  const toRehome = rubric ? questions.filter((question) => !isHomed(question)) : [];
  const homed = questions.filter(isHomed);
  const emptyLines = lines.filter((line) => !homed.some((question) => question.criterionKey === line.key));
  const problems = kitProblems(draft, rubric);
  const isFull = draft.questions.length >= MAX_KIT_QUESTIONS;

  const change = (patch: Partial<Draft>): void => {
    setDraft((previous) => ({ ...previous, ...patch }));
    setIsDirty(true);
  };
  const setQuestions = (next: readonly QuestionDraft[]): void => change({ questions: inLineOrder(next, lineKeys) });
  const setQuestion = (rowId: string, patch: Partial<QuestionDraft>): void => setQuestions(draft.questions.map((question) => (question.rowId === rowId ? { ...question, ...patch } : question)));
  const remove = (rowId: string): void => setQuestions(draft.questions.filter((question) => question.rowId !== rowId));

  /** A move among the lines' questions, in screen order: the question takes the line of the one it lands on. */
  function moveHomed(from: number, to: number): void {
    const target = homed[to];
    const moved = homed[from];
    if (!target || !moved) return;
    const next = moveItem(homed, from, to).map((question) => (question.rowId === moved.rowId ? { ...question, criterionKey: target.criterionKey } : question));
    setQuestions([...toRehome, ...next]);
  }

  /** To the end of another line's questions. */
  function moveToLine(rowId: string, criterionKey: string): void {
    const moved = draft.questions.find((question) => question.rowId === rowId);
    if (!moved) return;
    setQuestions([...draft.questions.filter((question) => question.rowId !== rowId), { ...moved, criterionKey }]);
  }

  const { attach, itemProps, handle } = useDragReorder<HTMLDivElement>(homed.length, moveHomed, !isReadOnly);

  function addTo(criterionKey: string): void {
    const question = newQuestion(criterionKey);
    setQuestions([...draft.questions, question]);
    setFocused(question.rowId);
  }

  function chooseRubric(familyId: string): void {
    // A question keeps its line only if the new rubric has it too; the rest wait in "To re-home" (keeping their old line, so
    // choosing the first rubric again brings them straight back).
    change({ rubricFamilyId: familyId });
  }

  function cancel(): void {
    if (!base) {
      release();
      void navigate({ to: '/admin/interview-kits' });
      return;
    }
    setDraft(draftOf(base));
    setIsDirty(false);
  }

  async function onSave(): Promise<void> {
    if (problems.length > 0 || isReadOnly || !draft.rubricFamilyId) return;
    setIsSaving(true);
    try {
      const saved = await save.mutateAsync({
        id: base?.id ?? null,
        version: base?.version ?? 0,
        body: {
          name: draft.name.trim(),
          description: draft.description.trim() || null,
          rubricFamilyId: draft.rubricFamilyId,
          questions: questions.map((question) => ({
            ...(question.key ? { key: question.key } : {}),
            questionText: question.questionText.trim(),
            guidance: question.guidance.trim() || null,
            criterionKey: question.criterionKey ?? '',
          })),
        },
      });
      setIsDirty(false);
      release();
      show({ tone: 'success', message: `${saved.name} saved as version ${saved.versionNumber}`, detail: base ? `Rounds using version ${base.versionNumber} keep it.` : "A job's round can pick it now." });
      void navigate({ to: '/admin/interview-kits/$kitId', params: { kitId: saved.id } });
    } catch (cause) {
      setIsSaving(false);
      show({ tone: 'danger', message: 'The interview kit was not saved', detail: errorMessage(cause) });
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLElement>): void {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && isDirty) {
      event.preventDefault();
      void onSave();
    }
  }

  const saveLabel = base ? `Save as version ${base.versionNumber + 1}` : 'Save kit';
  // Numbered straight through, as the interviewer reads them: the ones to re-home first, then line by line.
  const numberOf = (question: QuestionDraft): number => questions.indexOf(question) + 1;
  const labelOf = (criterionKey: string | null): string | null => lines.find((line) => line.key === criterionKey)?.label ?? null;
  const preview = questions
    .filter((question) => question.questionText.trim() !== '')
    .map((question) => ({ id: question.rowId, text: question.questionText.trim(), guidance: question.guidance.trim() || null, topic: labelOf(question.criterionKey) }));

  function questionRow(question: QuestionDraft, number: number, place: { readonly index: number; readonly count: number; readonly onMove: (to: number) => void } | null, dragIndex: number | null): ReactNode {
    const name = `question ${number}`;
    const isGuidanceOpen = question.guidance !== '' || openGuidance.has(question.rowId);
    const otherLines = lines.filter((line) => line.key !== question.criterionKey);
    return (
      <li key={question.rowId} className={styles.question} {...(dragIndex !== null ? itemProps(dragIndex) : {})}>
        <span className={styles.lineHandle}>{dragIndex !== null ? handle(dragIndex, name) : null}</span>
        <span className={styles.questionNumber} aria-hidden>
          {number}
        </span>
        <div className={styles.questionBody}>
          <Textarea
            aria-label={`Question ${number}`}
            rows={2}
            disabled={isReadOnly}
            value={question.questionText}
            autoFocus={focused === question.rowId && question.questionText === ''}
            placeholder="Walk me through a bank reconciliation you found difficult."
            onFocus={() => setFocused(question.rowId)}
            onChange={(event) => setQuestion(question.rowId, { questionText: event.target.value })}
          />
          {isGuidanceOpen ? (
            <Input
              aria-label={`What a good answer to question ${number} covers`}
              disabled={isReadOnly}
              value={question.guidance}
              placeholder="What a good answer covers"
              onFocus={() => setFocused(question.rowId)}
              onChange={(event) => setQuestion(question.rowId, { guidance: event.target.value })}
            />
          ) : (
            !isReadOnly && (
              <button type="button" className={styles.linkButton} onClick={() => setOpenGuidance(new Set([...openGuidance, question.rowId]))}>
                Add what a good answer covers
              </button>
            )
          )}
        </div>
        <span className={styles.lineMenu}>
          {!isReadOnly && (
            <MoveMenu
              label={name}
              index={place?.index ?? 0}
              count={place?.count ?? 1}
              onMove={(to) => place?.onMove(to)}
              places={{ heading: 'Move to the line', items: otherLines.map((line) => ({ label: line.label, onSelect: () => moveToLine(question.rowId, line.key) })) }}
              onRemove={() => remove(question.rowId)}
              removeLabel="Remove the question"
            />
          )}
        </span>
      </li>
    );
  }

  return (
    <div className={styles.kitLayout}>
      <section className={styles.editor} aria-label={base ? base.name : 'New interview kit'} onKeyDown={onKeyDown}>
        <header className={styles.editorHead}>
          <div className={styles.editorTitleRow}>
            <h2 className={styles.editorTitle}>{base ? base.name : 'New interview kit'}</h2>
            {base && <Chip tone="neutral">Version {base.versionNumber}</Chip>}
          </div>
          {canEdit && !isReadOnly && (
            <div className={styles.headActions}>
              {isDirty && <span className={problems.length > 0 ? styles.problemText : styles.quiet} role="status">{problems.length > 0 ? problems.join(' ') : 'Unsaved changes'}</span>}
              <Button variant="ghost" size="sm" disabled={!isDirty && base !== null} onClick={cancel}>
                Cancel
              </Button>
              <Button variant="solid" size="sm" disabled={!isDirty || problems.length > 0} isPending={save.isPending} onClick={() => void onSave()}>
                {saveLabel}
              </Button>
            </div>
          )}
        </header>

        {prompt}
        {isOlder && (
          <p className={styles.banner} role="status">
            This is version {base.versionNumber}, kept for the rounds that picked it. It can't be changed.{' '}
            {current && current.id !== base.id && (
              <Link to="/admin/interview-kits/$kitId" params={{ kitId: current.id }} className={styles.bannerLink}>
                Open version {current.versionNumber}, the current one
              </Link>
            )}
          </p>
        )}
        {isOvertaken && (
          <p className={styles.bannerDanger} role="alert">
            Someone saved a newer version while you were editing, so these changes can't be saved over it.{' '}
            {current && current.id !== base.id && (
              <Link to="/admin/interview-kits/$kitId" params={{ kitId: current.id }} className={styles.bannerLink}>
                Open version {current.versionNumber}
              </Link>
            )}
          </p>
        )}

        <div className={styles.form}>
          <FieldRow>
            <Field label="Name" isRequired>
              {({ inputId }) => <Input id={inputId} disabled={isReadOnly} value={draft.name} placeholder="Finance technical questions" onChange={(event) => change({ name: event.target.value })} />}
            </Field>
            <Field label="For the rubric" isRequired hint={rubric ? `${rubric.name} · v${rubric.versionNumber}. Each question sits under one of its lines.` : 'Each question sits under one of its lines.'}>
              {({ inputId, describedBy }) => (
                <Select
                  id={inputId}
                  describedBy={describedBy}
                  label="Rubric"
                  placeholder="Choose a rubric…"
                  disabled={isReadOnly}
                  value={rubric ? draft.rubricFamilyId : null}
                  options={rubrics.map((candidate) => ({ value: candidate.familyId, label: candidate.name, description: `${candidate.criteria.length} lines · v${candidate.versionNumber}` }))}
                  onChange={chooseRubric}
                />
              )}
            </Field>
          </FieldRow>
          <Field label="When to use it" isOptional>
            {({ inputId }) => <Textarea id={inputId} rows={2} disabled={isReadOnly} value={draft.description} placeholder="First round for accountants; pick 4–5 questions, not all of them" onChange={(event) => change({ description: event.target.value })} />}
          </Field>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHead}>
            <h3 className={styles.sectionTitle}>Questions, by rubric line</h3>
            <span className={emptyLines.length > 0 && rubric ? styles.warnText : styles.quiet}>
              {draft.questions.length} question{draft.questions.length === 1 ? '' : 's'}
              {rubric && emptyLines.length > 0 ? ` · ${emptyLines.length} line${emptyLines.length === 1 ? ' has' : 's have'} none` : ''}
            </span>
          </div>
          {!rubric ? (
            <p className={styles.quiet}>Choose the rubric above first: its lines are the groups the questions go under.</p>
          ) : (
            <div ref={attach} className={styles.groups}>
              {toRehome.length > 0 && (
                <section className={styles.rehome} aria-label="Questions to re-home">
                  <h4 className={styles.groupTitle}>
                    <TriangleAlert size={14} aria-hidden /> To re-home · {toRehome.length}
                  </h4>
                  <p className={styles.groupNote}>
                    {rubric.name} has no line for {toRehome.length === 1 ? 'this question' : 'these questions'}. Move each to a line with its menu, or remove it.
                  </p>
                  <ol className={styles.questions}>{toRehome.map((question) => questionRow(question, numberOf(question), null, null))}</ol>
                </section>
              )}
              {lines.map((line) => {
                const inLine = homed.filter((question) => question.criterionKey === line.key);
                return (
                  <section key={line.key} className={styles.group} aria-label={line.label}>
                    <div className={styles.groupHead}>
                      <h4 className={styles.groupTitle}>{line.label}</h4>
                      {inLine.length === 0 ? (
                        <span className={styles.warnText}>
                          <TriangleAlert size={14} aria-hidden /> No questions yet
                        </span>
                      ) : (
                        <span className={styles.quiet}>
                          {inLine.length} question{inLine.length === 1 ? '' : 's'}
                        </span>
                      )}
                      {!isReadOnly && !isFull && (
                        <Button size="sm" variant="ghost" icon={Plus} onClick={() => addTo(line.key)} aria-label={`Add a question for ${line.label}`}>
                          Add a question
                        </Button>
                      )}
                    </div>
                    {inLine.length > 0 && (
                      <ol className={styles.questions}>
                        {inLine.map((question, index) => {
                          const at = homed.indexOf(question);
                          const first = at - index;
                          return questionRow(question, numberOf(question), { index, count: inLine.length, onMove: (to) => moveHomed(at, first + to) }, at);
                        })}
                      </ol>
                    )}
                  </section>
                );
              })}
            </div>
          )}
          {isFull && !isReadOnly && <p className={styles.quiet}>A kit holds {MAX_KIT_QUESTIONS} questions at most.</p>}
        </div>
      </section>

      <aside className={styles.preview} aria-label="Preview: the interviewer's screen">
        <h3 className={styles.sectionTitle}>Preview — the interviewer's screen</h3>
        <div className={styles.previewSheet}>
          <div className={styles.previewHead}>
            <span className={styles.previewTitle}>Interview kit</span>
            <span className={styles.quiet}>{draft.name.trim() || 'New interview kit'}</span>
          </div>
          {preview.length === 0 ? <p className={styles.quiet}>This round has no suggested questions.</p> : <KitQuestionList questions={preview} highlightId={focused} />}
        </div>
        <p className={styles.quiet}>
          Interviewers open this in the interview with K. They don't mark the lines: each gives one overall mark from 1 to 5.
        </p>
      </aside>
    </div>
  );
}
